import React, { useState, useEffect } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import './PortfolioOverview.css';

const PortfolioOverview: React.FC = () => {
  const { state } = usePortfolio();
  const { portfolio } = state;
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Update timestamp when portfolio changes
  useEffect(() => {
    setLastUpdate(new Date());
  }, [portfolio.totalValue, portfolio.holdings]);

  const totalInvested = portfolio.holdings.reduce((sum, holding) => 
    sum + (holding.quantity * holding.averagePrice), 0
  );

  const totalUnrealizedPL = portfolio.holdings.reduce((sum, holding) => 
    sum + holding.unrealizedPL, 0
  );

  // Calculate total portfolio change from initial $1M
  const initialPortfolioValue = 1000000;
  const totalPortfolioChange = portfolio.totalValue - initialPortfolioValue;
  const totalPortfolioChangePercent = (totalPortfolioChange / initialPortfolioValue) * 100;

  // Calculate unrealized P/L percentage for holdings
  const totalUnrealizedPLPercent = totalInvested > 0 ? (totalUnrealizedPL / totalInvested) * 100 : 0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (percent: number) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  return (
    <div className="portfolio-overview">
      <div className="overview-header">
        <h2>Portfolio Overview</h2>
        <div className="last-updated">
          Last updated: {lastUpdate.toLocaleTimeString()}
          <span className="live-indicator"> 🔴 LIVE</span>
        </div>
      </div>

      <div className="overview-stats">
        <div className="stat-card primary">
          <div className="stat-header">
            <span className="stat-label">Total Portfolio Value</span>
            <span className="stat-icon">💼</span>
          </div>
          <div className="stat-value">{formatCurrency(portfolio.totalValue)}</div>
          <div className={`stat-change ${totalPortfolioChange >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(totalPortfolioChange)} ({formatPercent(totalPortfolioChangePercent)})
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Available Cash</span>
            <span className="stat-icon">💰</span>
          </div>
          <div className="stat-value">{formatCurrency(portfolio.cash)}</div>
          <div className="stat-subtitle">
            {((portfolio.cash / portfolio.totalValue) * 100).toFixed(1)}% of portfolio
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Invested Amount</span>
            <span className="stat-icon">📈</span>
          </div>
          <div className="stat-value">{formatCurrency(totalInvested)}</div>
          <div className="stat-subtitle">
            {portfolio.holdings.length} position{portfolio.holdings.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <span className="stat-label">Unrealized P/L</span>
            <span className="stat-icon">📊</span>
          </div>
          <div className={`stat-value ${totalUnrealizedPL >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(totalUnrealizedPL)}
          </div>
          <div className={`stat-subtitle ${totalUnrealizedPL >= 0 ? 'positive' : 'negative'}`}>
            {formatPercent(totalUnrealizedPLPercent)}
          </div>
        </div>
      </div>

      <div className="overview-chart">
        <div className="chart-placeholder">
          <div className="chart-icon">📊</div>
          <p>Portfolio Performance Chart</p>
          <small>TODO: Implement interactive chart with Chart.js or similar</small>
        </div>
      </div>
    </div>
  );
};

export default PortfolioOverview;
